import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createStoreThunk,
  deleteStoreThunk,
  updateStoreThunk,
  clearBranchError,
} from "../../../store/thunks/branchThunks";
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  XIcon,
} from "@heroicons/react/outline";
import { STORE_TYPES } from "constants/branch.constants";

const StoreList = ({ branchId, stores = [] }) => {
  const dispatch = useDispatch();
  const { error, loading } = useSelector((state) => state.branch);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingStoreId, setEditingStoreId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    storeType: "RETAIL",
    location: {
      mall: "",
      floor: "",
      shopNumber: "",
      area: "",
    },
    contact: {
      phoneNumber: "",
      email: "",
    },
  });

  const initialFormState = {
    name: "",
    storeType: "RETAIL",
    location: {
      mall: "",
      floor: "",
      shopNumber: "",
      area: "",
    },
    contact: {
      phoneNumber: "",
      email: "",
    },
  };

  // Clear form and editing state
  const resetForm = () => {
    setFormData(initialFormState);
    setEditingStoreId(null);
    setShowCreateForm(false);
  };

  // Clear error when component unmounts
  useEffect(() => {
    return () => {
      if (error) {
        dispatch(clearBranchError());
      }
    };
  }, [dispatch]);

  const storeTypeColors = {
    RETAIL: "bg-blue-100 text-blue-800",
    MALL: "bg-purple-100 text-purple-800",
    HIGH_STREET: "bg-green-100 text-green-800",
    COMMUNITY: "bg-yellow-100 text-yellow-800",
    AIRPORT: "bg-red-100 text-red-800",
    HOTEL: "bg-gray-100 text-gray-800",
  };

  const handleEditStore = (store) => {
    setEditingStoreId(store._id);
    setFormData({
      name: store.name || "",
      storeType: store.storeType || "RETAIL",
      location: {
        mall: store.location?.mall || "",
        floor: store.location?.floor || "",
        shopNumber: store.location?.shopNumber || "",
        area: store.location?.area || "",
      },
      contact: {
        phoneNumber: store.contact?.phoneNumber || "",
        email: store.contact?.email || "",
      },
    });
    setShowCreateForm(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStoreId) {
        await dispatch(
          updateStoreThunk({
            branchId,
            storeId: editingStoreId,
            storeData: formData,
          })
        ).unwrap();
      } else {
        await dispatch(
          createStoreThunk({
            branchId,
            storeData: formData,
          })
        ).unwrap();
      }
      resetForm();
    } catch (err) {
      // Error will be handled by the reducer
      console.error("Store operation failed:", err);
    }
  };

  const handleDeleteStore = async (storeId) => {
    if (window.confirm("Are you sure you want to delete this store?")) {
      try {
        await dispatch(deleteStoreThunk({ branchId, storeId })).unwrap();
      } catch (err) {
        console.error("Failed to delete store:", err);
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Stores ({stores.length})</h3>
        {!showCreateForm && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4" />
            Add Store
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <XIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => dispatch(clearBranchError())}
                className="inline-flex text-red-400 hover:text-red-500 focus:outline-none"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateForm && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-semibold mb-3">
            {editingStoreId ? "Edit Store" : "Create New Store"}
          </h4>
          <form onSubmit={handleFormSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Store Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Store Type
                </label>
                <select
                  value={formData.storeType}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      storeType: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {Object.entries(STORE_TYPES).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              {formData.storeType === "MALL" && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mall Name
                    </label>
                    <input
                      type="text"
                      value={formData.location.mall}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          location: { ...prev.location, mall: e.target.value },
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Floor
                    </label>
                    <input
                      type="text"
                      value={formData.location.floor}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          location: { ...prev.location, floor: e.target.value },
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Shop Number
                </label>
                <input
                  type="text"
                  value={formData.location.shopNumber}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      location: {
                        ...prev.location,
                        shopNumber: e.target.value,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Area (sq ft)
                </label>
                <input
                  type="number"
                  value={formData.location.area}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      location: { ...prev.location, area: e.target.value },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.contact.phoneNumber}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      contact: { ...prev.contact, phoneNumber: e.target.value },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.contact.email}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      contact: { ...prev.contact, email: e.target.value },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 ${
                  loading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {loading
                  ? "Processing..."
                  : editingStoreId
                  ? "Update Store"
                  : "Create Store"}
              </button>
            </div>
          </form>
        </div>
      )}

      {stores.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No stores added yet. Click "Add Store" to create one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {stores.map((store) => (
            <div key={store._id} className="bg-white border rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h4 className="font-semibold">{store.name}</h4>
                  <p className="text-sm text-gray-500">{store.code}</p>
                </div>
                <span
                  className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    storeTypeColors[store.storeType] ||
                    "bg-gray-100 text-gray-800"
                  }`}
                >
                  {STORE_TYPES[store.storeType] || store.storeType}
                </span>
              </div>

              <div className="space-y-1 text-sm text-gray-600">
                {store.location?.mall && (
                  <p>
                    📍 {store.location.mall}
                    {store.location.floor && `, ${store.location.floor}`}
                  </p>
                )}
                {store.location?.shopNumber && (
                  <p>Shop: {store.location.shopNumber}</p>
                )}
                {store.location?.area && (
                  <p>Area: {store.location.area} sq ft</p>
                )}
                {store.contact?.phoneNumber && (
                  <p>📞 {store.contact.phoneNumber}</p>
                )}
                {store.contact?.email && <p>✉️ {store.contact.email}</p>}
              </div>

              <div className="flex justify-end gap-2 mt-3 pt-3 border-t">
                <button
                  onClick={() => handleEditStore(store)}
                  className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeleteStore(store._id)}
                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StoreList;
